import React from "react";
import {
	PageContextState,
	PageContextType,
	UpdatePageContextFunction,
	defaultPageContextState,
} from "./PageContextType";

// Re-export types for convenience
export type { PageContextState, PageContextType, UpdatePageContextFunction };

/**
 * Default no-op update function for initial context value
 */
const defaultUpdateFunction: UpdatePageContextFunction = () => {};

/**
 * React Context for managing application-wide page state
 * Provides a tuple of [state, updateFunction] for consumers
 */
const PageContext = React.createContext<PageContextType>([
	defaultPageContextState,
	defaultUpdateFunction,
]);

export default PageContext;

/**
 * Helper function to update the page context from class components
 * Must be bound to a component with `this.context` set to PageContextType
 * @deprecated Consider using useContext hook with PageContext instead
 * @param contextObject - Partial state to merge with current state
 * @param updatePageContextDoneCallBack - Optional callback after update
 */
export function updatePageContext(
	this: { context: PageContextType },
	contextObject: Partial<PageContextState>,
	updatePageContextDoneCallBack: (() => void) | null = null
): void {
	this.context[1](
		{
			...this.context[0],
			...contextObject,
		},
		updatePageContextDoneCallBack
	);
}

/**
 * Async helper function to update the page context from class components
 * Must be bound to a component with `this.context` set to PageContextType
 * @deprecated Consider using useContext hook with PageContext instead
 * @param contextObject - Partial state to merge with current state
 * @param updatePageContextDoneCallBack - Optional callback after update (unused, resolve is called instead)
 * @returns Promise that resolves when the context update is complete
 */
export function updatePageContextAsync(
	this: { context: PageContextType },
	contextObject: Partial<PageContextState>,
	updatePageContextDoneCallBack: (() => void) | null = null
): Promise<void> {
	return new Promise((resolve) => {
		this.context[1](
			{
				...this.context[0],
				...contextObject,
			},
			resolve
		);
	});
}

/**
 * Helper function to get the current page context state from class components
 * Must be bound to a component with `this.context` set to PageContextType
 * @deprecated Consider using useContext hook with PageContext instead
 * @returns The current page context state
 */
export function getPageContext(this: { context: PageContextType }): PageContextState {
	return this.context[0];
}
